import { FC, useRef } from 'react';
import useFilters from '@/app/hooks/useFilters';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { UITable } from '@/shared/ui/ui-table';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import UIAutocomplete from '../../shared/ui/ui-autocmplete';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import { Currency, GetClientsList } from '../../app/api/types';
import { UIGridItem } from '../../shared/ui/ui-grid-item';
import { useGetPaymentsDocumentsQuery } from '@/app/api/paymentsApi';
import ComponentWithLoader from '@/shared/ComponentWithLoader/ComponentWithLoader';
import { Link } from 'react-router-dom';
import { triada } from '@/app/helpers';
import { MoneyFlowModal } from '@/features/money-flow-modal/index';
import { MoneyFlowModalRef } from './types';
import { CURRENCY_OPTIONS } from '@/constants';

interface Props {
  clientsList: GetClientsList[] | undefined;
}

const defaultFilters = {
  from: '',
  to: dayjs().format('DD.MM.YYYY'),
  clientTin: '',
  page: 1,
  pageSize: 10,
  currency: 'UZS',
};

export const FactorMoneyFlow: FC<Props> = ({ clientsList }) => {
  const { t } = useTranslation();
  const { updateFilters, nonEmptyFilters: filters } = useFilters({
    defaultFilters,
    saveToUrl: true,
  });
  const moneyFlowModalRef = useRef<MoneyFlowModalRef>(null);

  const { data, isLoading } = useGetPaymentsDocumentsQuery({
    ...filters,
    page: Number(filters.page),
    pageSize: Number(filters.pageSize),
  });

  return (
    <ComponentWithLoader>
      <Stack spacing={2.5}>
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' spacing={3}>
            <Stack direction='row' spacing={1}>
              <DatePicker
                label={t('from') + ':'}
                format='DD.MM.YYYY'
                value={
                  dayjs(filters.from, 'DD.MM.YYYY').isValid()
                    ? dayjs(filters.from, 'DD.MM.YYYY')
                    : null
                }
                onChange={(value) =>
                  updateFilters({
                    from: dayjs(value).isValid()
                      ? dayjs(value).format('DD.MM.YYYY')
                      : '',
                  })
                }
              />
              <DatePicker
                label={t('to') + ':'}
                format='DD.MM.YYYY'
                value={
                  dayjs(filters.to, 'DD.MM.YYYY').isValid()
                    ? dayjs(filters.to, 'DD.MM.YYYY')
                    : null
                }
                onChange={(value) =>
                  updateFilters({
                    to: dayjs(value).isValid()
                      ? dayjs(value).format('DD.MM.YYYY')
                      : '',
                  })
                }
              />
            </Stack>

            <UIAutocomplete
              value={
                clientsList?.find((item) => item.tin === filters.clientTin) ??
                null
              }
              options={clientsList ?? []}
              TextFieldProps={{
                label: t('client_name'),
                sx: { width: 300 },
              }}
              getOptionLabel={(option) => option.shortName}
              onChange={(_event, value) => {
                updateFilters({ clientTin: value?.tin });
              }}
            />

            <UIAutocomplete
              value={CURRENCY_OPTIONS.find(
                (item) => item.value === filters.currency
              )}
              options={CURRENCY_OPTIONS}
              TextFieldProps={{
                label: t('currency'),
                sx: { width: 125 },
              }}
              getOptionLabel={(option) => {
                return t(option.label);
              }}
              onChange={(_event, value) => {
                updateFilters({ currency: value?.value as Currency });
              }}
              disableClearable
            />
          </Stack>

          {/* <Stack direction='row' spacing={1}>
            <Button variant='contained' color='inherit'>
              {t('export')}
            </Button>
          </Stack> */}
        </Stack>

        <Box>
          <Grid container spacing={2}>
            <UIGridItem
              title={t('payments_from_buyers') + ':'}
              subtitle={triada(String(data?.data.amountFromClient || 0))}
              gridItemProps={{ xs: 3 }}
              gradient
            />
            <UIGridItem
              title={t('payments_to_sellers') + ':'}
              subtitle={triada(String(data?.data.amountToSeller || 0))}
              gridItemProps={{ xs: 3 }}
            />
            <UIGridItem
              title={t('comission') + ':'}
              subtitle={triada(String(data?.data.commissionAmount || 0))}
              gridItemProps={{ xs: 3 }}
            />
            <UIGridItem
              title={t('average_percent') + ':'}
              subtitle={triada(String(data?.data.averageDiscount || 0)) + '%'}
              gridItemProps={{ xs: 3 }}
            />
          </Grid>
        </Box>

        <EmptyDataPlaceholder
          isEmpty={!data?.data.paymentDocs}
          title={t('no_transactions')}
          isLoading={isLoading}
        >
          <UITable
            data={data?.data.paymentDocs ?? []}
            columns={[
              {
                label: t('transaction_id'),
                render: ({ unique }) => (
                  <Typography
                    onClick={() => moneyFlowModalRef.current?.open(unique)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: 14,
                      color: '#1675e0',
                    }}
                  >
                    {unique}
                  </Typography>
                ),
              },
              {
                label: t('deal_number'),
                render: ({ dealId }) => (
                  <Link
                    style={{ textDecoration: 'none' }}
                    to={`/factor/requests/${dealId}?backUrl=${window.location.pathname}${window.location.search}`}
                    target='_blank'
                  >
                    {dealId}
                  </Link>
                ),
              },
              {
                label: t('transaction_date'),
                key: 'createdAt',
              },
              {
                label: t('payer'),
                key: 'payer.name',
              },
              {
                label: t('recipient'),
                key: 'recipient.name',
              },
              {
                label: t('transaction_type'),
                key: 'paymentTypeName',
              },
              {
                label: t('sum'),
                render: ({ amount }) => triada(amount),
              },
            ]}
            PaginationProps={{
              count: data?.totalPages || 0,
              page: Number(filters.page),
              onChange: (_e, page) => {
                updateFilters({ page });
              },
              sizes: {
                onRowsPerPageChange: (rowsPerPage) =>
                  updateFilters({ pageSize: +rowsPerPage, page: 1 }),
                currentRowsPerPage: String(filters.pageSize),
              },
            }}
          />
        </EmptyDataPlaceholder>
        <MoneyFlowModal ref={moneyFlowModalRef} />
      </Stack>
    </ComponentWithLoader>
  );
};
